import { LocalizedString } from '@shared/types/query';
import { i18nConfig, getLocalizationInfo } from '@/project.config';

// EWM-854 — resolve a commercetools `_url` LocalizedString to a path for the
// active locale, with a fallback chain instead of a bare index.
//
// The AU/NZ storefronts share ONE commercetools catalogue, authored in ONE
// language. Verified against electus-dev on 2026-08-10: all 750 categories
// carry `slug` under `en-AU` only, zero under `en-NZ`. Because
// `ProductMapper.generateLocalizedUrl` builds `_url` by iterating the
// category's OWN `slug` keys, `_url` is `{ 'en-AU': '/…' }` and never gains an
// `en-NZ` entry — so every `_url[activeLocale]` read resolved to `undefined`
// on NZ. On the PDP that emptied the breadcrumb path, `buildCategoryCrumbs`
// returned `[]`, and the whole category trail vanished (0 of 8,928 published
// products rendered a trail on NZ; the PLP was unaffected because it derives
// its crumbs from the URL segments, not from `_url`).
//
// Falling back to the en-AU path is not a cosmetic patch: NZ genuinely serves
// the en-AU category slugs. `buildCategorySlugWhere` (EWM-396) already ORs the
// slug lookup across every declared language for exactly this reason, so the
// href this produces resolves to a real NZ PLP.
//
// Mirrors `LocalizedValue.getLocalizedValue`'s read fallback on the BFF
// (requested → project default → first authored) so both sides of the wire
// agree on what "the value for this locale" means. Kept as a shared util
// because four separate call sites had independently written the unguarded
// `_url?.[locale] ?? ''` form.
export const resolveLocalizedUrl = (url: LocalizedString | undefined, locale: string): string => {
  if (!url) return '';

  // `getLocalizationInfo` already degrades an unknown locale to the default.
  const candidates = [getLocalizationInfo(locale).locale, i18nConfig.defaultLocale];

  for (const candidate of candidates) {
    if (url[candidate]) return url[candidate];
  }

  // Neither the requested nor the default language is authored — take whatever
  // the catalogue does carry rather than emitting a dead empty href.
  return Object.values(url).find(Boolean) ?? '';
};

// EWM-854 — first resolvable category path in a flat category list, for the
// "Browse products" CTAs on the empty cart and the purchase-lists page. Both
// had independently written `items.find((cat) => cat._url?.[locale])`, so on NZ
// no category ever matched and both buttons pointed at `/`.
//
// Loops rather than `.map().find(Boolean)` so resolution short-circuits on the
// first hit, matching the original `.find()`.
export const firstLocalizedUrl = (categories: Array<{ _url?: LocalizedString }>, locale: string): string => {
  for (const category of categories) {
    const url = resolveLocalizedUrl(category._url, locale);
    if (url) return url;
  }

  return '';
};
